import { Component, OnDestroy, OnInit, ChangeDetectorRef, ViewChild, WritableSignal, signal, computed, Signal, ChangeDetectionStrategy } from '@angular/core'
import * as fromCoreStore from '@app/core/store'
import * as fromUserStore from '@app/users/store'
import * as fromShopStore from '@app/shop/store'
import * as fromSidebarStore from '@app/sidebar/store'
import { Store } from '@ngrx/store'
import { Observable, Subject } from 'rxjs'
import { finalize, groupBy, map, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators'
import { ShopOrderService } from '@app/shop/services/shopOrder.service'
import { User } from '@app/shared/models/user.model'
import { Contact, FOLLOW_UP_RESOURCE, RedirectService } from '@app/shared'
import { OpenModal } from '@app/modals/store'
import { ToastHelper } from '@app/core/services'
import { ShopShopOrder } from '@app/shop/models/shopOrder.model'
import { popupService } from '@app/shared/services/popup.service'
import { NgxPopperjsTriggers, NgxPopperjsPlacements, NgxPopperjsContentComponent } from 'ngx-popperjs'
import { maskitoTransform } from '@maskito/core'
import { debounceTime } from 'rxjs/operators'
import { ShopService } from '@app/shop/models/shopService.model'
import { ShopOrderAccount, ShopQuickBooksCustomerChange, ShopQuickBooksCustomerLink } from '@app/shop/models/shopOrderAccount.model'
import { ShopQuickBooksItem } from '@app/shop/models/shopQuickBooksItem.model'
import { environment } from 'environments/environment'

interface _ShopService extends ShopService {
  _selected?: boolean
}

@Component({
  selector: 'app-shop-order-details',
  templateUrl: './shop-order-details.component.html',
  styleUrls: ['./shop-order-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ShopOrderDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('selectedStatus', { static: false }) selectedStatus: NgxPopperjsContentComponent
  @ViewChild('_prevOdometer', { static: false }) _prevOdometer: NgxPopperjsContentComponent
  locked$: Observable<boolean>
  // QuickBooks invoices of this shop order
  shopAccounts$: Observable<ShopOrderAccount[]>
  quickBooksShopConnected$: Observable<boolean>
  // Services billed on the order's QuickBooks invoices (empty while it has none)
  qbInvoices: WritableSignal<ShopOrderAccount[]> = signal([])
  // Total Shop Fee row: while shop QuickBooks is connected, or when invoices already have Shop Fee lines.
  qbShowShopFee: Signal<boolean> = computed(() => this.qbConnected() || this.qbShopFeeTotal() > 0)
  // Cents of the Shop Fee lines of all its invoices, like the services' amounts (app-price takes cents)
  qbShopFeeTotal: Signal<number> = computed(() => this.qbInvoices().reduce((sum, i) => sum + (i.shopFee || 0), 0))
  qbInvoicedServiceIds: Signal<Set<string>> = computed(() => new Set(this.qbInvoices().flatMap(i => i.serviceIds || []).map(String)))
  // The order truck's shop QuickBooks customer
  qbCustomer$: Observable<ShopQuickBooksCustomerLink>
  qbCustomerSaving$: Observable<boolean>
  qbConnected: WritableSignal<boolean> = signal(false)
  // Part rows pick a QuickBooks item (inventory / non-inventory) with app-search (resource SHOP_PART: up to
  // 50 catalog items matching the text, searched on the server); labor is a mechanic's hand work and stays
  // free text
  // The QuickBooks items the order's part rows are linked to, and the linked ids QuickBooks no longer
  // has (deleted, or made inactive)
  qbLinkedItems: WritableSignal<Map<string, ShopQuickBooksItem>> = signal(new Map())
  qbMissingIds: WritableSignal<Set<string>> = signal(new Set())
  // Services on an invoice that changed on the order since it was saved (billed snapshot on its lines)
  qbChangedServiceIds: Signal<Set<string>> = computed(() => {
    const services = new Map(this.services().map(s => [String(s._id), s]))
    const changed = new Set<string>()
    for (const invoice of this.qbInvoices()) {
      for (const line of invoice.lines || []) {
        for (const billed of line.billed || []) {
          const service = services.get(String(billed.serviceId))
          if (service && this.billedDiffers(billed, service)) {
            changed.add(String(billed.serviceId))
          }
        }
      }
    }
    return changed
  })
  qbUrl = environment.qbUrl
  NgxPopperjsTriggers = NgxPopperjsTriggers
  NgxPopperjsPlacements = NgxPopperjsPlacements
  destroyed$ = new Subject<boolean>()
  set currentUser(val:User) {
    if(val){
      this._currentUser = val
      if(val.permissions?.['shop_update']){
        this.canUpdate = true
      }
    }
  }
  _currentUser: User
  get currentUser(){
    return this._currentUser
  }
  canUpdate: boolean
  currentShopOrder$: Observable<any>
  currentShopOrder: ShopShopOrder
  FOLLOW_UP_RESOURCE = FOLLOW_UP_RESOURCE
  users
  expenses
  userIndex = '0'
  services: WritableSignal<_ShopService[]> = signal([])
  readonly _services: Signal<_ShopService[]> = computed(() => this.services().filter(service => this.filterValue() ? (service.desc || '').toLowerCase()?.includes(this.filterValue()) : true))
  filterValue: WritableSignal<string> = signal('')

  currentFiles = []
  inputMasks = {
    phone: ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
  }
  type = 'phone';

  maskTransform = maskitoTransform

  serviceTypes = []

  statuses = [
    { _id: 'NEW', name: 'NEW' },
    { _id: 'ACTIVE', name: 'ACTIVE' },
    { _id: 'COMPLETED', name: 'COMPLETED' },
    { _id: 'CLOSED', name: 'CLOSED' },
  ]

  currentShopOrderStatuses = [
    { _id: 'NEW', name: 'NEW' },
    { _id: 'ACTIVE', name: 'ACTIVE' },
    { _id: 'COMPLETED', name: 'COMPLETED' },
    { _id: 'CLOSED', name: 'CLOSED' },
    { _id: 'ON_HOLD', name: 'ON-HOLD' },
  ]

  selectedServices: _ShopService[] = []
  loadingService = false

  currentServiceTypes

  editUnit = false

  totalLabors = 0
  totalParts = 0
  totalAmount = 0

  selectedService: _ShopService = null;
  sortableOptions

  isMouseDown: boolean = false;
  hasMoved: boolean = false;

  private descSubject = new Subject<{ event: Event, service: any }>();
  // Part rows: text and QuickBooks link are saved together, per row, from the row's state at save time
  private partSubject = new Subject<_ShopService>()
  // Linked item ids already read (or being read) for the badges
  private qbLinkedRequested = new Set<string>()

  constructor(
    private store: Store<fromShopStore.State>,
    private autStore: Store<fromUserStore.State>,
    private coreStore: Store<fromCoreStore.State>,
    private notify: ToastHelper,
    private popupService: popupService,
    private cdr: ChangeDetectorRef,
    private redirectService: RedirectService,
    private shopOrderService: ShopOrderService
  ) {
    this.descSubject.pipe(
      debounceTime(600)
    ).subscribe(({ event, service }) => {
      this.updateServiceDesc(event, service)
    })
    this.partSubject.pipe(
      groupBy(service => service._id),
      mergeMap(rowChanges => rowChanges.pipe(debounceTime(600))),
      takeUntil(this.destroyed$)
    ).subscribe(service => {
      this.updateService({ _id: service._id, desc: service.desc || '', qbItem: service.qbItem || null })
    })
  }

  getMask = () => ({
    mask: this.inputMasks[this.type] || false
  })

  onMouseDown(event: MouseEvent) {
    this.isMouseDown = true
    this.hasMoved = false
  }

  onMouseMove(event: MouseEvent) {
    if (this.isMouseDown) {
      this.hasMoved = true
    }
  }


  ngOnInit(): void {
    let oldServices: _ShopService[] = []
    this.sortableOptions = {
      filter: '.not-drag',
      preventOnFilter: false,
      onEnd: _ => {
        const newServicesArr: _ShopService[] = this._services().filter(s => s).map((s, index) => { return { ...s, index: index + 1 } })

        if (JSON.stringify(newServicesArr) !== JSON.stringify(oldServices.filter(s => s))) {
          this.store.dispatch(new fromShopStore.ShopServiceMultiUpdate({ _orderId: this.currentShopOrder._id, changes: newServicesArr as ShopService[] }))
        }
      },
      onStart: _ => {
        oldServices = [...this._services()]
      }
    }

    this.userIndex = this.redirectService.getUrlIndex()

    this.shopAccounts$ = this.store.select(fromShopStore.selectShopOrderAccounts)
    this.quickBooksShopConnected$ = this.store.select(fromShopStore.selectQuickBooksShopConnected)
    this.qbCustomer$ = this.store.select(fromShopStore.selectShopQuickBooksCustomer)
    this.qbCustomerSaving$ = this.store.select(fromShopStore.selectShopQuickBooksCustomerSaving)
    this.shopAccounts$.pipe(takeUntil(this.destroyed$)).subscribe(invoices => {
      this.qbInvoices.set(invoices || [])
      this.cdr.markForCheck()
    })
    this.quickBooksShopConnected$.pipe(takeUntil(this.destroyed$)).subscribe(connected => {
      this.qbConnected.set(!!connected)
      if (connected) {
        this.loadLinkedParts(this.services())
      }
      this.cdr.markForCheck()
    })

    this.locked$ = this.store.select(fromShopStore.selectCurrentShopOrderId).pipe(
      switchMap(id => this.store.select(fromCoreStore.checkLockedItemLock(id))
        .pipe(
          map(lock => !lock.lockedByCurrentTab)
        )
      )
    )

    this.autStore
      .select(fromUserStore.getUser)
      .pipe(
        takeUntil(this.destroyed$),
        map(user => {
          this.currentUser = user
        })
      )
      .subscribe()

    this.store.select(fromShopStore.selectShopOrder).pipe(
      takeUntil(this.destroyed$),
      map(order => {
        if (order && order?._id) {
          let uniqueIds = {}
          let uniqueUnits = order.unit.filter(obj => {
            if (!uniqueIds[obj.name]) {
              uniqueIds[obj.name] = true
              return true
            }
            return false
          })
          order.unit = uniqueUnits
        }
        this.currentShopOrder = order
        this.cdr.detectChanges()
        this.coreStore.select(fromCoreStore.selectFilesByResource('SHOP_ORDER', [this.currentShopOrder?._id])).pipe(
          takeUntil(this.destroyed$),
          map(files => {
            this.currentFiles = files
          })
        )
          .subscribe()

      })
    )
      .subscribe()

    this.store.select(fromShopStore.selectAllShopServices).pipe(
      takeUntil(this.destroyed$),
      map(services => {
        if (services) {
          this.services.set(services)
          this.loadLinkedParts(services)
          this.countAmounts(services)
          this.cdr.detectChanges()
        }
      })
    )
      .subscribe()

    this.store
      .select(fromShopStore.selectShopServicesTypes)
      .pipe(
        takeUntil(this.destroyed$),
        map(serviceTypes => {
          if (serviceTypes) {
            this.serviceTypes = serviceTypes
            this.currentServiceTypes = Object.entries(serviceTypes).map(([key, value]) => {
              return { _id: key, name: value }
            })
          }
        })
      )
      .subscribe()

    this.store.select(fromSidebarStore.selectAllUsers).pipe(
      takeUntil(this.destroyed$),
      map(users => {
        this.users = users
      })
    ).subscribe()

    this.store
      .select(fromShopStore.getLoading)
      .pipe(
        takeUntil(this.destroyed$),
        map(loading => {
          this.loadingService = loading
          return loading
        })
      )
      .subscribe()
  }

  updateShopOrder(order, populate = {}) {
    if (order?.unit?.driver) {
      delete order.unit.driver
    }
    this.store.dispatch(new fromShopStore.ShopOrderUpdate({ order, populate }))
  }

  countAmounts(services) {
    this.totalLabors = 0
    this.totalParts = 0
    this.totalAmount = 0
    services.map(s => {
      if (s?.amount) {
        if (s.type === 'LABOR') {
          this.totalLabors += Number(s?.amount) || 0
        } else {
          this.totalParts += Number(s?.amount) || 0
        }
        this.totalAmount += Number(s?.amount) || 0
      }
    })
  }

  toggleEditMode(service: any, event?) {
    if (event) {
      event.stopPropagation()
    }

    if (this.isMouseDown && !this.hasMoved && this.canUpdate) {
      const entering = this.selectedService?._id !== service?._id
      this.selectedService = service
      if (entering) {
        this.focusClickedField(event)
      }
    }

    this.isMouseDown = false
    this.hasMoved = false
  }

  /**
   * A row opens its edit view on the first click; put the cursor straight into the field of the column that
   * was clicked, so editing a value takes one click instead of two.
   */
  private focusClickedField(event?: Event) {
    const row = event?.currentTarget as HTMLElement
    const cell = (event?.target as HTMLElement)?.closest?.('td')
    if (!row || !cell || cell.parentElement !== row) {
      return
    }
    const column = Array.prototype.indexOf.call(row.children, cell)
    this.cdr.detectChanges()
    setTimeout(() => {
      const field = row.children[column]?.querySelector<HTMLInputElement | HTMLTextAreaElement>(
        'textarea:not([disabled]), input:not([type=checkbox]):not([disabled]):not([readonly])'
      )
      if (field) {
        // Just the cursor, at the end: selecting everything made the first key replace the whole text
        field.focus()
        // A select (the part picker) opens its options on click, not on focus
        if (field.closest('app-select')) {
          field.click()
        }
      }
    })
  }

  updateService(service) {
    this.store.dispatch(new fromShopStore.ShopServiceUpdate({ _orderId: this.currentShopOrder._id, ...service }))
  }

  mathFloor(amount) {
    return Number(Math.floor(amount))
  }

  onSelectAll() {
    if (this.selectedServices?.length === this._services()?.length) {
      this.selectedServices = []
      this.services.update(s => s.map(v => ({ ...v, _selected: false })))
    } else {
      this.selectedServices = this._services()
      this.services.update(s => s.map(v => this.selectedServices.find(_s => _s._id === v._id) ? ({ ...v, _selected: true }) : v))
    }
  }

  updateUnit(data) {
    let change: any = {}
    for (let unit of this.currentShopOrder?.unit) {

      if (unit?._id == data?.value) {
        change = { _id: data._id, unit: { ...unit } }
        break
      }
    }

    for (let s of this.services()) {
      if (s?._id == data._id) {
        s.unit = { ...change.unit }
      }
    }

    this.updateService(change)
  }

  handleClick(event: Event) {
    event.stopPropagation()
  }

  setShop(shopId, shopName) {
    this.currentShopOrder.shop = { _id: shopId, name: shopName }
    this.updateShopOrder(this.currentShopOrder, { _shop: 1 })
  }

  removeShop(event) {
    this.currentShopOrder.shop = {}
    this.updateShopOrder(this.currentShopOrder)
  }

  setCustomer(id) {
    this.currentShopOrder.customerId = id
    this.updateShopOrder(this.currentShopOrder, { _customer: 1 })
  }

  setDriver(id) {
    this.currentShopOrder.driverId = id
    this.updateShopOrder(this.currentShopOrder, { _driver: 1, samsaraOdometer: 1 })
  }

  searchService(value: string) {
    this.filterValue.set(value?.toLowerCase())
  }

  removeCustomer(event) {
    this.currentShopOrder.customerId = null
    this.currentShopOrder.unit = []
    this.currentShopOrder.odometer = 0
    this.updateShopOrder(this.currentShopOrder, { _customer: 1, _unit: 1 })
  }

  async setOdometer(event) {

    if (event === '') {
      event = 0
    }
    event = Number(event)

    if (event === this.currentShopOrder?.odometer || (event == 0 && !this.currentShopOrder?.odometer)) {
      return
    }

    if (this._prevOdometer) {
      this._prevOdometer.hide()
    }
    this.currentShopOrder._prevOdometer = this.currentShopOrder._prevOdometer || 0
    if (event < this.currentShopOrder._prevOdometer && (await this.popupService.confirm(`You set odometer less than on the previous order (${this.currentShopOrder._prevOdometer})`))) {
      this.currentShopOrder.odometer = event
      this.updateShopOrder(this.currentShopOrder, { samsaraOdometer: 1 })
      return
    } else if (event >= this.currentShopOrder._prevOdometer) {
      this.currentShopOrder.odometer = event
      this.updateShopOrder(this.currentShopOrder, { samsaraOdometer: 1 })
    } else {
      return
    }
  }

  patchDesc(event) {
    this.currentShopOrder.desc = event.target.value
    this.updateShopOrder(this.currentShopOrder)
  }

  setMechanic(contact) {
    let mechanic = {
      _id: contact._id,
      name: contact.name
    }
    this.currentShopOrder['mechanic'] = mechanic
    this.updateShopOrder(this.currentShopOrder)
  }

  setUnit({ name, _id, equipmentType }) {
    let unit = {
      name,
      _id,
      unitType: equipmentType
    }
    this.currentShopOrder['unit'].push(unit)
    const data = this.currentShopOrder
    delete data.odometer
    this.updateShopOrder({ _id: data._id, unit: data.unit }, { _unit: 1, samsaraOdometer: 1 })
  }

  replaceUnit({ name, _id, equipmentType }) {
    let unit = {
      name,
      _id,
      unitType: equipmentType
    }
    const data = { _id: this.currentShopOrder._id, odometer: 0, unit: [unit] }
    this.editUnit = false
    this.updateShopOrder(data, { _unit: 1, samsaraOdometer: 1 })
    setTimeout(() => {
      this.coreStore.dispatch(
        new fromShopStore.GetShopOrderServices({ _orderId: this.currentShopOrder._id })
      )
    }, 1000)
  }

  removeUnit(_id) {
    this.updateShopOrder({
      ...this.currentShopOrder,
      unit: this.currentShopOrder?.unit.filter(unit => unit._id !== _id),
      odometer: 0
    }, { _unit: 1, samsaraOdometer: 1 })
  }

  setStatus(event) {
    let status = event
    event = this.currentShopOrder?.status
    if (status === 'ACTIVE' || status === 'COMPLETED' || status === 'CLOSED') {
      if (!this.currentShopOrder?.driverId) {
        return this.notify.error('Please Select Driver')
      }
      if (!this.currentShopOrder?.arrivalDate || !this.currentShopOrder?.departureDate) {
        return this.notify.error('Please Set Arrival/Departure Date')
      }
      if (!this.currentShopOrder?.odometer) {
        return this.notify.error('Please Select Odometer')
      }
    }
    if (status === 'COMPLETED' || status === 'CLOSED') {
      if (this.services().find(s => s?.status !== "COMPLETED")) {
        return this.notify.error('Services must be completed')
      }
      if (!this.currentShopOrder?.completedAt) {
        return this.notify.error('Please Set Completed Date')
      }
    }
    if (status === 'CLOSED') {
      if (this.totalAmount === 0) {
        return this.notify.error('Please Set Amounts')
      }
      if (!this.currentShopOrder?.accounting?.invoiceNumber || !this.currentShopOrder?.accounting?.invoiceDate) {
        return this.notify.error('Please Set Invoice Number/Date')
      }

      if (!this.currentFiles.length) {
        return this.notify.error('Please Upload Invoice')
      }
    }
    event = status
    this.currentShopOrder.status = status
    this.updateShopOrder(this.currentShopOrder)

    if (this.selectedStatus) {
      this.selectedStatus.hide()
    }
  }

  setAccounting(field, event) {
    if (event === undefined || event === null) return
    this.currentShopOrder.accounting = { ...this?.currentShopOrder?.accounting }
    this.currentShopOrder.accounting[field] = event
    this.updateShopOrder(this.currentShopOrder)
  }

  // Text a part row gets when its QuickBooks item is picked, e.g. "CC2742 FLEETGUARD COOLANT CONCENTRATE"
  partDesc(item: ShopQuickBooksItem) {
    return `${item.name || item.fullyQualifiedName} ${item.description || ''}`.trim()
  }

  // Free text typed in a part row (a custom part: a picked QuickBooks part shows read only, removed to type)
  onPartTextChange(service: _ShopService, text: string) {
    service.desc = text
    this.partSubject.next(service)
  }

  /**
   * The QuickBooks items of the order's linked part rows, read once per id: the badge warns about a
   * row whose item QuickBooks no longer has (deleted, or made inactive)
   */
  private loadLinkedParts(services: _ShopService[]) {
    if (!this.qbConnected()) {
      return
    }
    const ids = [...new Set((services || []).filter(s => s?.type !== 'LABOR' && s?.qbItem?.qbId).map(s => s.qbItem.qbId))]
      .filter(id => !this.qbLinkedRequested.has(id))
    if (!ids.length) {
      return
    }
    ids.forEach(id => this.qbLinkedRequested.add(id))
    this.shopOrderService.getQuickBooksCatalog({ ids: ids.join(',') }).pipe(take(1), takeUntil(this.destroyed$)).subscribe({
      next: resp => {
        // Disconnected meanwhile: nothing is known about the items
        if (!resp?.data?.connected) {
          ids.forEach(id => this.qbLinkedRequested.delete(id))
          return
        }
        const found: ShopQuickBooksItem[] = resp.data.items || []
        const foundIds = new Set(found.map(i => i.qbId))
        this.qbLinkedItems.update(items => new Map([...items, ...found.map(i => [i.qbId, i] as [string, ShopQuickBooksItem])]))
        this.qbMissingIds.update(missing => new Set([...missing, ...ids.filter(id => !foundIds.has(id))]))
        this.cdr.markForCheck()
      },
      // Unknown: no warning, read again on the next change of the rows
      error: () => ids.forEach(id => this.qbLinkedRequested.delete(id))
    })
  }

  private setLinkedItem(item: ShopQuickBooksItem) {
    this.qbLinkedRequested.add(item.qbId)
    this.qbLinkedItems.update(items => new Map(items).set(item.qbId, item))
    this.qbMissingIds.update(missing => {
      const next = new Set(missing)
      next.delete(item.qbId)
      return next
    })
  }

  // Remove the QuickBooks part picked on a row: the row is unlinked and its text cleared, to pick another
  // part or type a custom one. Quantity and price stay.
  removeServiceQbItem(service: _ShopService) {
    const changes = { qbItem: null, desc: '' }
    this.updateService({ _id: service._id, ...changes })
    Object.assign(service, changes)
  }

  /**
   * Link a part row to a QuickBooks item: the row takes the item's name and description, and its price
   * when the item has one.
   */
  setServiceQbItem(service: _ShopService, item: ShopQuickBooksItem) {
    const qty = service.qty || 1
    // No price in QuickBooks: 0 for the user to fill in, never the previous part's price
    const rate = item.unitPrice || 0
    const changes = {
      qbItem: { qbId: item.qbId, name: item.fullyQualifiedName, sku: item.sku || '', type: item.type },
      desc: this.partDesc(item),
      qty,
      rate,
      amount: Math.round(qty * rate)
    }
    this.setLinkedItem(item)
    this.updateService({ _id: service._id, ...changes })
    Object.assign(service, changes)
    this.countAmounts(this.services())
    this.refreshPickedItemPrice(service, item)
  }

  // The catalog is a local copy: re-read the picked item from QuickBooks and take its current price when
  // it changed. On failure (QuickBooks unreachable) the catalog price stays.
  private refreshPickedItemPrice(service: _ShopService, picked: ShopQuickBooksItem) {
    this.shopOrderService.getQuickBooksCatalogItem(picked.qbId).pipe(take(1), takeUntil(this.destroyed$)).subscribe({
      next: resp => {
        const live: ShopQuickBooksItem = resp?.data
        // Still the same pick (the user may have changed the row meanwhile)
        if (service.qbItem?.qbId !== picked.qbId) {
          return
        }
        // Made inactive in QuickBooks (its way of deleting) counts as gone
        if (!live || (live as ShopQuickBooksItem & { active?: boolean }).active === false) {
          this.notify.error(`${picked.fullyQualifiedName || picked.name} is no longer in QuickBooks`)
          this.qbMissingIds.update(missing => new Set(missing).add(picked.qbId))
          this.cdr.markForCheck()
          return
        }
        this.setLinkedItem(live)
        if (live.unitPrice && live.unitPrice !== service.rate) {
          const qty = service.qty || 1
          const changes = { rate: live.unitPrice, amount: Math.round(qty * live.unitPrice) }
          this.updateService({ _id: service._id, ...changes })
          Object.assign(service, changes)
          this.countAmounts(this.services())
          this.cdr.markForCheck()
        }
      },
      error: () => null
    })
  }

  // Labor rows can't be linked to a QuickBooks item: switching a linked row to labor unlinks it
  setServiceType(service: _ShopService, type: string) {
    this.updateService({ _id: service._id, type, ...(type === 'LABOR' && service.qbItem ? { qbItem: null } : {}) })
    service.type = type
    if (type === 'LABOR') {
      service.qbItem = null
    }
    this.countAmounts(this.services())
  }

  // "Invoice #2021", or "The invoice" for one QuickBooks has no number for
  private invoiceName(invoice: ShopOrderAccount) {
    return invoice.docNumber ? `Invoice #${invoice.docNumber}` : 'The invoice'
  }

  async deleteQuickBooksInvoice(invoice: ShopOrderAccount) {
    const confirmed = await this.popupService.confirm(
      `Delete ${invoice.docNumber ? 'invoice #' + invoice.docNumber : 'this invoice'}? It is deleted in QuickBooks too, and its services can be invoiced again.`
    )
    if (confirmed) {
      this.store.dispatch(new fromShopStore.DeleteShopOrderInvoice({ shopOrderId: this.currentShopOrder._id, accountId: invoice._id }))
    }
  }

  // Invoices being synced from QuickBooks (their Sync button spins)
  qbSyncing = signal<Set<string>>(new Set())

  // Like freight's Sync: take the invoice as it is now in QuickBooks (it's normally kept up to date by webhooks)
  syncQuickBooksInvoice(invoice: ShopOrderAccount) {
    const shopOrderId = this.currentShopOrder._id
    this.qbSyncing.update(ids => new Set(ids).add(invoice._id))
    this.shopOrderService
      .syncInvoice(shopOrderId, invoice._id)
      .pipe(
        finalize(() => this.qbSyncing.update(ids => { const next = new Set(ids); next.delete(invoice._id); return next })),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: resp => {
          if (resp?.data?.deleted) {
            this.notify.ok(`${this.invoiceName(invoice)} no longer exists in QuickBooks and was removed`)
            this.store.dispatch(new fromShopStore.DeleteShopOrderInvoiceSuccess({ accountId: invoice._id }))
            this.store.dispatch(new fromShopStore.ShopOrderUpdateSuccess({ id: shopOrderId, changes: { accounting: resp.data.accounting } }))
            return
          }
          this.notify.ok(`${this.invoiceName(invoice)} synced from QuickBooks`)
          this.store.dispatch(new fromShopStore.UpdateShopOrderInvoiceSuccess({ account: resp.data.account }))
        },
        error: err => this.notify.error(err?.message || err)
      })
  }

  // Added after the order was invoiced and on no invoice yet: highlighted, and offered when an invoice is edited
  isAddedAfterInvoice(service: _ShopService) {
    if (!this.qbInvoices().length || this.qbInvoicedServiceIds().has(String(service?._id))) {
      return false
    }
    // A row older than the first invoice was left off it (a $0 part, or removed in the invoice window), not
    // added since; rows without a date count as added
    const created = service?.createdAt ? new Date(service.createdAt).getTime() : NaN
    const first = this.firstInvoiceAt()
    return !Number.isFinite(created) || !first || created > first
  }

  private firstInvoiceAt: Signal<number> = computed(() => {
    const times = this.qbInvoices().map(i => (i.createdAt ? new Date(i.createdAt).getTime() : NaN)).filter(Number.isFinite)
    return times.length ? Math.min(...times) : null
  })

  // On an invoice, but changed since it was saved: highlighted too; editing the invoice updates its line
  isChangedAfterInvoice(service: _ShopService) {
    return this.qbChangedServiceIds().has(String(service?._id))
  }

  // Same comparison as the Backend (ShopOrderAccountController.serviceChanged)
  private billedDiffers(billed: { qty: number, rate: number, amount: number, desc: string, qbItemId?: string }, service: _ShopService) {
    return (
      (Number(billed.qty) || 0) !== (Number(service.qty) || 0) ||
      Math.round(Number(billed.rate) || 0) !== Math.round(Number(service.rate) || 0) ||
      Math.round(Number(billed.amount) || 0) !== Math.round(Number(service.amount) || 0) ||
      (billed.desc || '').trim() !== (service.desc || '').trim() ||
      (billed.qbItemId || '') !== (service.qbItem?.qbId || '')
    )
  }

  // Old part descriptions typed in a textarea can have line breaks; the one-line picker would drop them
  isMultilineCustomPart(service: _ShopService) {
    return !service?.qbItem?.qbId && (service?.desc || '').includes('\n')
  }



  /**
   * A part added after invoicing whose amount comes to $0: the invoice leaves it out (Backend
   * draftLines skips lines without an amount), so the row warns about it
   */
  isZeroNewPart(service: _ShopService) {
    if (service?.type === 'LABOR' || !this.isAddedAfterInvoice(service)) {
      return false
    }
    const amount = Math.round(Number(service.amount) || 0) || Math.round((Number(service.qty) || 0) * (Number(service.rate) || 0))
    return !amount
  }

  // The order's truck: shop invoices bill one QuickBooks customer per truck
  get orderTruckId(): string {
    return (this.currentShopOrder?.unit || []).find(unit => unit?.unitType === 'TRUCK')?._id || null
  }

  // Link the order's truck to a shop QuickBooks customer picked from the search, or unlink it
  changeQuickBooksCustomer(change: ShopQuickBooksCustomerChange) {
    this.store.dispatch(new fromShopStore.ChangeShopQuickBooksCustomer({ shopOrderId: this.currentShopOrder._id, change }))
  }

  async unlinkQuickBooksCustomer(customer: ShopQuickBooksCustomerLink) {
    const confirmed = await this.popupService.confirm(
      `Unlink truck ${customer.truck?.unitNumber} from QuickBooks customer "${customer.qbCustomer?.name}"? Nothing changes in QuickBooks; it can't be invoiced until a customer is chosen again.`
    )
    if (confirmed) {
      this.changeQuickBooksCustomer({ type: 'unlink' })
    }
  }

  // Create (no accountId) or open an existing QuickBooks invoice in the shop invoice modal
  openQuickBooksInvoice(accountId?: string) {
    this.store.dispatch(
      new OpenModal({
        type: 'SHOP_INVOICE',
        props: {
          shopOrderId: this.currentShopOrder._id,
          accountId,
          canUpdate: this.canUpdate,
          windowClass: 'order-accounts-item-modal'
        }
      })
    )
  }

  deleteShopOrder() {
    this.store.dispatch(new fromShopStore.DeleteShopOrder({ _id: this.currentShopOrder._id }))
  }

  generatePdf() {
    this.store.dispatch(new fromShopStore.GetShopOrderPDF({ order: this.currentShopOrder, query: {} }))
  }

  generateBlank() {
    this.store.dispatch(new fromShopStore.GetShopOrderPDF({ order: this.currentShopOrder, query: { _blank: 1 } }))
  }

  openPreview = (service) =>
    this.store.dispatch(
      new OpenModal({
        type: 'SHOP_SERVICE',
        props: {
          _id: service?._id,
          _orderId: this.currentShopOrder._id
          // windowClass : "shop-details-modal"
        },
      })
    )

  addService() {
    this.store.dispatch(new fromShopStore.CreateShopService({ item: { _orderId: this.currentShopOrder._id, status: "NEW", type: 'LABOR', index: this.services()?.length ? this.services()?.length + 1 : 1 }, order: this.currentShopOrder }))
  }

  // updateServiceDesc(event, service) {
  //   this.updateService({ _id: service._id, desc: event.target.value })
  // }

  updateServiceDesc(event: Event, service: any) {
    this.updateService({ _id: service._id, desc: (event.target as HTMLTextAreaElement).value })
  }

  onDescChange(event: Event, service: any) {
    this.descSubject.next({ event, service })
  }

  setServiceMechanic(contact: Contact, service: Partial<_ShopService>) {
    let mechanic: {
      _id: string,
      name: string
    } = null
    if (contact) {
      mechanic = {
        _id: contact._id,
        name: contact.name
      }
    }

    service = { _id: service._id, mechanic }
    this.updateService(service)
  }

  selectService = (service, event?) => {
    if (event) {
      event.stopPropagation()
    }
    if (this.selectedServices.findIndex(x => x._id === service._id) < 0) {
      this.selectedServices.push(service)
      this.services.update((s) => s.map(v => service._id === v._id ? ({ ...v, _selected: true }) : v))
    }
    else {
      this.selectedServices = this.selectedServices.filter(x => x._id != service._id)
      this.services.update((s) => s.map(v => service._id === v._id ? ({ ...v, _selected: false }) : v))
    }
  }

  unselectAllSelectedService() {
    this.selectedServices = []
    this.services.update(s => s.map(v => ({ ...v, _selected: false })))
  }

  setServicesStatus(event) {
    if (!event) return

    this.selectedServices.map(s => {
      s.status = event
      return s
    })
    this.store.dispatch(new fromShopStore.ShopServiceMultiUpdate({ _orderId: this.currentShopOrder._id, changes: this.selectedServices }))
    this.unselectAllSelectedService()
  }

  moveServices() {
    const servicesIds = this.selectedServices.map(s => s._id)
    const data: Partial<ShopShopOrder> = {
      customerId: this?.currentShopOrder?.customerId,
      shop: this?.currentShopOrder?.shop,
      unit: this?.currentShopOrder?.unit,
      driverId: this?.currentShopOrder?.driverId,
    }
    this.coreStore.dispatch(
      new fromShopStore.CreateShopOrder({ ...data, moveServices: servicesIds })
    )
    this.unselectAllSelectedService()
  }

  clone() {
    const data = {
      customerId: this?.currentShopOrder?.customerId,
      shop: this?.currentShopOrder?.shop,
      unit: this?.currentShopOrder?.unit,
    }
    this.filterValue.set('')

    this.coreStore.dispatch(
      new fromShopStore.CreateShopOrder(data)
    )
  }

  async deleteAllSelectedService() {
    if (await this.popupService.confirm('Are you sure you want to delete selected services?')) {
      this.store.dispatch(new fromShopStore.DeleteManyShopServices({ deleteData: { ids: this.selectedServices.map(t => t._id), _orderId: this.currentShopOrder._id }, order: this.currentShopOrder }))
      this.services.update(services => {
        return services
          .filter(s => s)
          .map((s, index) => { return { ...s, index: index + 1 } })
      })
      this.store.dispatch(new fromShopStore.ShopServiceMultiUpdateSuccess(this.services()))
      this.selectedServices = []
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true)
    this.destroyed$.complete()
  }
}
